import React, { useState, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { RefreshCw, Type as TypeIcon } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// pdfjs worker — served from CDN so we skip webpack copy-plugin plumbing.
// Version pinned to the exact package.json entry to avoid version-skew.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Two-tier PDF viewer for the Script Audit Studio.
 *   • Top: horizontal thumbnail strip (all pages, click to select).
 *   • Bottom: full-size render of the selected page only.
 * Auth is pushed through the `?token=` query param on `url`; the caller
 * is responsible for building that URL. Bumping `bustKey` remounts the
 * <Document> so a preview refresh forces a hard reload.
 */
export default function PdfPreviewPane({ url, refAppraisalId, onRefresh, focusedButton, bustKey }) {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(null);
  // iter297b — Auto-fit preview container to the rendered page height so
  // there's no trailing grey/white space below the page footer. Captured
  // per-render via `onRenderSuccess` from react-pdf.
  const [renderedPageH, setRenderedPageH] = useState(null);

  const onDocLoad = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPageNumber(prev => (prev > n ? 1 : prev));
    setLoadError(null);
  }, []);

  const onDocLoadError = useCallback((err) => {
    setLoadError(err?.message || String(err) || 'PDF failed to load');
  }, []);

  // Options must be memoised — every re-render of the parent otherwise
  // creates a new object and forces the PDF to re-fetch.
  const docOptions = useMemo(() => ({
    // Empty on purpose — no extra fonts / cmaps needed for our PDFs.
  }), []);

  return (
    <div className="col-span-12 lg:col-span-5 bg-white border border-[#E2E2DF] rounded-md overflow-hidden flex flex-col" data-testid="pdf-preview-pane">
      <div className="px-3 py-2 border-b border-[#E2E2DF] bg-slate-50 flex items-center gap-2">
        <TypeIcon size={12} className="text-slate-400" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Live PDF preview</span>
        {refAppraisalId && <span className="text-[10px] font-mono text-slate-400 truncate">· ref appraisal {refAppraisalId.slice(-6)}</span>}
        {numPages > 0 && (
          <span className="text-[10px] font-mono text-slate-500 ml-2" data-testid="pdf-page-indicator">
            page {pageNumber} / {numPages}
          </span>
        )}
        {focusedButton}
        <button
          onClick={onRefresh}
          className="ml-1 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 border border-[#316536] text-[#316536] rounded hover:bg-[#316536] hover:text-white transition-colors"
          title="Refresh PDF preview (⌘/Ctrl+R)"
          data-testid="pdf-refresh-btn"
        >
          <RefreshCw size={11} />
          <span>Refresh</span>
        </button>
      </div>

      {!url ? (
        <div className="flex-1 flex items-center justify-center text-xs text-slate-400">Loading reference appraisal…</div>
      ) : loadError ? (
        <div className="flex-1 flex flex-col items-center justify-center text-xs text-rose-600 p-6 text-center">
          <div className="font-bold mb-1">Preview failed to load</div>
          <div className="text-slate-500 font-mono text-[10px] max-w-full break-words">{loadError}</div>
          <button onClick={onRefresh} className="mt-3 px-3 py-1 border border-rose-300 rounded text-rose-700 hover:bg-rose-50">Retry</button>
        </div>
      ) : (
        <Document
          key={bustKey}
          file={url}
          onLoadSuccess={onDocLoad}
          onLoadError={onDocLoadError}
          options={docOptions}
          loading={<div className="flex-1 flex items-center justify-center text-xs text-slate-400">Rendering PDF…</div>}
          className="flex-1 flex flex-col min-h-0"
        >
          {/* Thumbnail strip — horizontal, scrollable, click to jump */}
          <div className="border-b border-[#E2E2DF] bg-slate-100/60 px-2 py-2 overflow-x-auto flex-shrink-0" data-testid="pdf-thumbnail-strip">
            <div className="flex gap-2 flex-nowrap">
              {Array.from({ length: numPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  data-testid={`pdf-thumb-${n}`}
                  onClick={() => setPageNumber(n)}
                  className={`flex-shrink-0 border-2 rounded overflow-hidden transition-all ${n === pageNumber ? 'border-[#316536] shadow-md ring-2 ring-[#316536]/20' : 'border-slate-300 hover:border-slate-500 opacity-70 hover:opacity-100'}`}
                  title={`Page ${n}`}
                >
                  <div className="pointer-events-none">
                    <Page pageNumber={n} width={72} renderTextLayer={false} renderAnnotationLayer={false} />
                  </div>
                  <div className={`text-center text-[9px] font-mono py-0.5 ${n === pageNumber ? 'bg-[#316536] text-white' : 'bg-white text-slate-500'}`}>{n}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Big page — auto-fits container height to the rendered page so
              there's no trailing whitespace below the footer strip
              (iter297b). Falls back to `flex-1` until first render. */}
          <div
            className="min-h-0 overflow-auto bg-slate-200 flex justify-center p-4"
            data-testid="pdf-big-page"
            style={renderedPageH ? { flex: `0 0 ${renderedPageH + 32}px` } : { flex: '1 1 0%' }}
          >
            {numPages > 0 && (
              <Page
                pageNumber={pageNumber}
                width={640}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onRenderSuccess={({ height }) => setRenderedPageH(Math.ceil(height))}
                className="shadow-lg bg-white"
              />
            )}
          </div>
        </Document>
      )}
    </div>
  );
}
